import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  TablePagination,
  CircularProgress,
  IconButton,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const DailyReportDowntimeTable = ({ reportData, loading, error }) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("unitName");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedData = useMemo(() => {
    if (!reportData || !Array.isArray(reportData)) return [];

    return [...reportData].sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case "unitName":
          aValue = a.unitName || "";
          bValue = b.unitName || "";
          break;
        case "timeStart":
          aValue = new Date(a.timeStart || 0).getTime();
          bValue = new Date(b.timeStart || 0).getTime();
          break;
        case "timeEnd":
          aValue = new Date(a.timeEnd || 0).getTime();
          bValue = new Date(b.timeEnd || 0).getTime();
          break;
        case "faultCode":
          aValue = a.faultCode || "";
          bValue = b.faultCode || "";
          break;
        case "fault":
          aValue = a.fault || "";
          bValue = b.fault || "";
          break;
        case "downtime":
          aValue = Number(a.downtime) || 0;
          bValue = Number(b.downtime) || 0;
          break;
        case "customerTime":
          aValue = Number(a.customerTime) || 0;
          bValue = Number(b.customerTime) || 0;
          break;
        case "companyTime":
          aValue = Number(a.companyTime) || 0;
          bValue = Number(b.companyTime) || 0;
          break;
        case "ctrlType":
          aValue = a.ctrlType || "";
          bValue = b.ctrlType || "";
          break;
        default:
          return 0;
      }

      // Handle string comparison
      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue
          .toLowerCase()
          .localeCompare(bValue.toLowerCase());
        return order === "asc" ? comparison : -comparison;
      }

      // Handle numeric/date comparison
      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [reportData, order, orderBy]);

  const paginated = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  if (loading) {
    return (
      <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{ py: 4, textAlign: "center", color: "red", fontWeight: "bold" }}
      >
        {error}
      </Box>
    );
  }

  const columns = [
    { id: "unitName", label: "Unit Name", hideOnMobile: false },
    { id: "ctrlType", label: "Controller", hideOnMobile: true },
    { id: "timeStart", label: "Date Start", hideOnMobile: true },
    { id: "timeEnd", label: "Date End", hideOnMobile: true },
    { id: "faultCode", label: "SD", hideOnMobile: true },
    { id: "fault", label: "SD Enum", hideOnMobile: true },
    { id: "downtime", label: "Downtime(Hrs)", hideOnMobile: true },
    { id: "customerTime", label: "Customer(Hrs)", hideOnMobile: true },
    { id: "companyTime", label: "Precision(Hrs)", hideOnMobile: true },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Typography fontWeight="bold" sx={{ fontSize: 18, mb: 1, px: 2 }}>
        Daily Downtime Report
      </Typography>

      <TableContainer sx={{ pl: 2, height: { xs: "50vh", md: "63vh" } }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {/* Mobile expand column */}
              <TableCell
                sx={{
                  display: { xs: "table-cell", md: "none" },
                  textAlign: "center",
                  width: 50,
                  py: 1,
                }}
              >
                Details
              </TableCell>

              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    display: column.hideOnMobile
                      ? { xs: "none", md: "table-cell" }
                      : "table-cell",
                    py: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={() => handleRequestSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginated.length > 0 ? (
              paginated.map((row, index) => (
                <React.Fragment key={`${row.unitId}-${index}`}>
                  <TableRow
                    hover
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                    }}
                  >
                    {/* Expand button mobile */}
                    <TableCell
                      sx={{
                        display: { xs: "table-cell", md: "none" },
                        textAlign: "center",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          toggleRowExpansion(`${row.unitId}-${index}`)
                        }
                        sx={{ color: "white" }}
                      >
                        {expandedRows[`${row.unitId}-${index}`] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </TableCell>

                    <TableCell>{row.unitName}</TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {row.ctrlType}
                    </TableCell>
                    <TableCell
                      sx={{
                        display: { xs: "none", md: "table-cell" },
                        width: 185,
                      }}
                    >
                      {row.timeStart}
                    </TableCell>
                    <TableCell
                      sx={{
                        display: { xs: "none", md: "table-cell" },
                        width: 185,
                      }}
                    >
                      {row.timeEnd}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {row.faultCode}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {row.fault}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {row.downtime}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {row.customerTime}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {row.companyTime}
                    </TableCell>
                  </TableRow>

                  {/* Mobile collapse */}
                  <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                    <TableCell colSpan={2} sx={{ p: 0, border: 0 }}>
                      <Collapse
                        in={expandedRows[`${row.unitId}-${index}`]}
                        unmountOnExit
                      >
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor:
                              index % 2 === 0 ? "#2f3136" : "#3a3c43",
                          }}
                        >
                          <Typography variant="body2">
                            Controller: {row.ctrlType}
                          </Typography>
                          <Typography variant="body2">
                            Date Start: {row.timeStart}
                          </Typography>
                          <Typography variant="body2">
                            Date End: {row.timeEnd}
                          </Typography>
                          <Typography variant="body2">
                            SD: {row.faultCode}
                          </Typography>
                          <Typography variant="body2">
                            SD Enum: {row.fault}
                          </Typography>
                          <Typography variant="body2">
                            Downtime(Hrs): {row.downtime}
                          </Typography>
                          <Typography variant="body2">
                            Customer(Hrs): {row.customerTime}
                          </Typography>
                          <Typography variant="body2">
                            Precision(Hrs): {row.companyTime}
                          </Typography>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                  No data
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sortedData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        sx={{ mt: 1 }}
      />
    </Box>
  );
};

export default DailyReportDowntimeTable;
